/**
 * TutorialPanel Component
 * 
 * Displays tutorial steps, hints, and educational content.
 * Provides controls for navigating through the tutorial and using hints.
 */

import { Button } from '@components/common/Button';
import { getTutorialStep, generateHint } from '@core/tutorialSteps';
import styles from './TutorialPanel.module.scss';

export interface TutorialPanelProps {
  /** Current tutorial step */
  currentStep: number;
  /** Total number of steps */
  totalSteps: number;
  /** Whether to show hint */
  showHint: boolean;
  /** Number of hints remaining */
  hintsRemaining: number;
  /** Current room ID */
  currentRoom: number;
  /** Solution path */
  solutionPath: number[];
  /** Visited rooms */
  visited: Set<number>;
  /** Whether tutorial mode is active */
  isTutorialMode: boolean;
  /** Callback to advance to next step */
  onNextStep: () => void;
  /** Callback to use a hint */
  onUseHint: () => void;
  /** Callback to hide hint */
  onHideHint: () => void;
  /** Callback to exit tutorial */
  onExitTutorial: () => void;
}

export const TutorialPanel = ({
  currentStep,
  totalSteps,
  showHint,
  hintsRemaining,
  currentRoom,
  solutionPath,
  visited,
  isTutorialMode,
  onNextStep,
  onUseHint,
  onHideHint,
  onExitTutorial,
}: TutorialPanelProps) => {
  const step = getTutorialStep(currentStep);

  if (!step) {
    return null;
  }

  const hintText = generateHint(currentRoom, solutionPath, visited);
  const isLastStep = currentStep === totalSteps - 1;

  return (
    <div className={styles.tutorialPanel}>
      <div className={styles.header}>
        <div className={styles.progress}>
          <span className={styles.stepIndicator}>
            Step {currentStep + 1} of {totalSteps}
          </span>
          <div className={styles.progressBar}>
            <div 
              className={styles.progressFill}
              style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
            />
          </div>
        </div>
        <button
          className={styles.closeButton}
          onClick={onExitTutorial}
          aria-label="Exit tutorial"
        >
          ✕
        </button>
      </div>

      <div className={styles.content}>
        <h2 className={styles.title}>{step.title}</h2>
        <div className={styles.explanation}>
          {step.explanation.split('\n\n').map((paragraph, index) => (
            <p key={index}>{paragraph}</p>
          ))}
        </div>

        {showHint && (
          <div className={styles.hintBox}>
            <div className={styles.hintHeader}>
              <span className={styles.hintIcon}>💡</span>
              <span className={styles.hintTitle}>Hint</span>
            </div>
            <p className={styles.hintText}>{hintText}</p>
            <Button
              variant="secondary"
              size="small"
              onClick={onHideHint}
            >
              Got it
            </Button>
          </div>
        )}
      </div>

      <div className={styles.actions}>
        <div className={styles.leftActions}>
          {!showHint && (
            <Button
              variant="secondary"
              onClick={onUseHint}
              disabled={!isTutorialMode && hintsRemaining <= 0}
            >
              💡 Hint {isTutorialMode ? '(∞)' : `(${hintsRemaining})`}
            </Button>
          )}
        </div>

        <div className={styles.rightActions}>
          {step.expectedAction === null && (
            <Button
              variant="primary"
              onClick={isLastStep ? onExitTutorial : onNextStep}
            >
              {isLastStep ? 'Start Playing!' : 'Next →'}
            </Button>
          )}
          {step.expectedAction !== null && (
            <div className={styles.actionPrompt}>
              <span className={styles.promptIcon}>👉</span>
              <span>Make a move to continue</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};